<script lang="ts">
  import CalendarName from './CalendarName.svelte';
  import IconButton from './IconButton.svelte';
  import Icon from './Icon.svelte';
  import LocalBadge from './LocalBadge.svelte';
  import { config, ui, events, focus, search, effectiveFeedTz, zoom, layout, timelineEventsFor, rowInk, lightRowInk } from '../lib/state.svelte';
  import { today, todayStartMs } from '../lib/today.svelte';
  import { getMatchCountByFeed } from '../lib/search-state.svelte';
  import { smoothBehavior } from '../lib/motion';
  import { dateToPx, focusAnchorOffset } from '../lib/layout';
  import { clock } from '../lib/clock.svelte';
  import { formatTime, formatTzDiff, formatUpdatedAgo, isDaylight, tzOffsetMinutesVsDisplay, dayLimitMinutes } from '../lib/format';
  import { longPress, createLongPress } from '../lib/haptics';
  import { categoryIcon } from '../lib/icons';
  import type { CalendarFeed, DisplayEvent, Timezone } from '../lib/types';
  import { FEED_ERROR_ICON_PX } from '../lib/types';
  import { textBaselineSnap, textLayout, watchTextLayout } from '../lib/baseline-snap.svelte';

  type Strip = { left: number; width: number };
  type Props = {
    feed: CalendarFeed;
    visibleEvents: DisplayEvent[];
    rangeStart: Date;
    pxPerDay: number;
    scrollEl: HTMLElement | undefined;
    rowIndex: number;
    // Weekend tint + day-blocking hatch strips (window-filtered, content px), so
    // the same pattern the row bodies show runs continuously through the header.
    weekendStrips: { left: number; width: number; past: boolean }[];
    thickStrips: Strip[];
    thinStrips: Strip[];
    // Day- and month-separator x positions (window-filtered, content px), so the
    // vertical rules continue up through the header like the full-height bands
    // Timeline draws over the row bodies (.day-col / .month-line).
    dayLines: { px: number; past: boolean }[];
    monthLines: { px: number; past: boolean }[];
  };
  const {
    feed, visibleEvents, rangeStart, pxPerDay, scrollEl, rowIndex,
    weekendStrips, thickStrips, thinStrips, dayLines, monthLines,
  }: Props = $props();

  // The events prev/next navigation steps through: the same merged, start-sorted
  // list the row's pills render, so focus.eventIndex lines up with the pills (a
  // consecutive-day run is one nav step). The header's count badge still counts
  // the raw visibleEvents so it reflects the true number of events.
  const navEvents = $derived(timelineEventsFor(feed.id));

  const headerLongPress = createLongPress(500);
  const charmLongPress = createLongPress(500);

  // This row is "in focus" (matches Row's isFocusedRow) — drives the inked
  // borders and the revealed prev/next nav.
  const isRowFocused = $derived(focus.feedId === feed.id);

  // Expanding/collapsing (or focusing) a row also puts it in focus, and lights
  // its ink (which fades on its own: rowInk in state.svelte.ts) — directly, as
  // re-focusing the same row changes nothing App's effect would see.
  function focusThisRow(): void {
    focus.feedId = feed.id;
    focus.eventIndex = -1;
    lightRowInk(feed.id);
  }
  const isInked = $derived(rowInk.feedId === feed.id);

  // A tap anywhere on the header (outside its own controls) collapses/expands
  // this row; guard against the long-press so a focus gesture doesn't also toggle.
  function toggleCollapsed(): void {
    if (headerLongPress.didFire()) return;
    const target = config.feeds.find((f) => f.id === feed.id);
    if (target) target.collapsed = !target.collapsed;
    focusThisRow();
  }

  // Long-press to focus this row: collapse every other row and expand this one.
  // A second long-press (when this row is already the only expanded one) reverses
  // it, expanding every row again.
  function focusRowToggle(): void {
    const soloExpanded =
      !feed.collapsed && config.feeds.every((f) => f.id === feed.id || f.collapsed);
    for (const f of config.feeds) {
      f.collapsed = soloExpanded ? false : f.id !== feed.id;
    }
    focusThisRow();
  }

  // Header taps/long-presses ignore the header's own interactive controls (nav,
  // category charm, warning, scratch-add) so those keep their own behaviour.
  function fromHeaderControl(e: Event): boolean {
    const el = e.target as HTMLElement | null;
    return !!el?.closest('.actions, .charm-btn, .warning-btn, .scratch-add');
  }

  function onHeaderClick(e: MouseEvent): void {
    if (fromHeaderControl(e)) return;
    toggleCollapsed();
  }

  function onHeaderPointerDown(e: PointerEvent): void {
    if (fromHeaderControl(e)) return;
    headerLongPress.start(focusRowToggle);
  }

  function onHeaderPointerEnd(): void {
    headerLongPress.cancel();
  }

  // Mouse hover mirrors the tap/click focus model: hovering the header focuses
  // the row, moving off it (onto anything that isn't this header) drops focus.
  function onHeaderPointerEnter(e: PointerEvent): void {
    if (e.pointerType !== 'mouse') return;
    focusThisRow();
  }

  function onHeaderPointerLeave(e: PointerEvent): void {
    headerLongPress.cancel();
    if (e.pointerType === 'mouse' && focus.feedId === feed.id) {
      focus.feedId = null;
      focus.eventIndex = -1;
    }
  }

  function openInSettings(): void {
    // Only auto-edit: startEdit() scrolls the feed card to the top of the list
    // (block:'start'), matching the menu edit. Setting settingsScrollToFeedId too
    // would add a competing block:'nearest' scroll that leaves it mid-list.
    ui.settingsAutoEditFeedId = feed.id;
    ui.settingsOpen = true;
  }

  function openAddEvent(): void {
    if (typeof window === 'undefined') return;
    // Preselect this local lane in the create-event modal (the + only shows on
    // scratchpad rows); cleared when the modal closes.
    ui.addEventFeedId = feed.id;
    window.dispatchEvent(new CustomEvent('cal:open-add-event'));
  }

  function focusAndScrollTo(sorted: DisplayEvent[], nextIdx: number): void {
    const ev = sorted[nextIdx];
    if (!ev) return;
    if (rowIndex >= 0) {
      focus.feedId = feed.id;
      focus.eventIndex = nextIdx;
    }
    if (scrollEl) {
      const px = dateToPx(ev.start, rangeStart, pxPerDay);
      // Same focus anchor the timeline's own scrolls use, so stepping through a
      // row's events parks each one where the marker and today line rest.
      const offset = focusAnchorOffset({
        clientWidth: scrollEl.clientWidth,
        scrollportLeft: scrollEl.getBoundingClientRect().left,
        zoomNavRight: layout.zoomNavRight,
        weekGapMid: layout.weekGapMid,
      });
      scrollEl.scrollTo({ left: Math.max(0, px - offset), behavior: smoothBehavior() });
    }
  }

  function jumpRelative(direction: -1 | 1): void {
    if (navEvents.length === 0) return;
    const sorted = navEvents;
    const focusedHere =
      focus.feedId === feed.id && focus.eventIndex >= 0 && focus.eventIndex < sorted.length;
    let nextIdx: number;
    if (!focusedHere) {
      // First click on prev/next anchors at the boundary around today
      // — first future event (for next) or most recent past (for prev).
      const todayMs = (e: { allDay: boolean }): number => todayStartMs(e.allDay);
      if (direction === 1) {
        nextIdx = sorted.findIndex((e) => e.start.getTime() > todayMs(e));
        if (nextIdx === -1) nextIdx = sorted.length - 1;
      } else {
        nextIdx = -1;
        for (let i = sorted.length - 1; i >= 0; i--) {
          if (sorted[i]!.start.getTime() < todayMs(sorted[i]!)) { nextIdx = i; break; }
        }
        if (nextIdx === -1) nextIdx = 0;
      }
    } else {
      const cur = focus.eventIndex;
      if (direction === 1) {
        nextIdx = cur >= sorted.length - 1 ? 0 : cur + 1;
      } else {
        nextIdx = cur <= 0 ? sorted.length - 1 : cur - 1;
      }
    }
    focusAndScrollTo(sorted, nextIdx);
  }

  function jumpToEnd(direction: -1 | 1): void {
    if (navEvents.length === 0) return;
    const nextIdx = direction === 1 ? navEvents.length - 1 : 0;
    focusAndScrollTo(navEvents, nextIdx);
  }

  const NAV_LONGPRESS_MS = 500;
  const NAV_FLASH_MS = 400;
  let navFlash: 'prev' | 'next' | null = $state(null);
  let navPressTimer: ReturnType<typeof setTimeout> | null = null;
  let navLongFired = false;

  function startNavPress(direction: -1 | 1): void {
    if (navCount <= 1) return;
    navLongFired = false;
    if (navPressTimer) clearTimeout(navPressTimer);
    navPressTimer = setTimeout(() => {
      navPressTimer = null;
      navLongFired = true;
      longPress();
      jumpToEnd(direction);
      navFlash = direction === 1 ? 'next' : 'prev';
      setTimeout(() => {
        if (navFlash === (direction === 1 ? 'next' : 'prev')) navFlash = null;
      }, NAV_FLASH_MS);
    }, NAV_LONGPRESS_MS);
  }

  function cancelNavPress(): void {
    if (navPressTimer) {
      clearTimeout(navPressTimer);
      navPressTimer = null;
    }
  }

  function handleNavClick(direction: -1 | 1): void {
    if (navCount <= 1) return;
    if (navLongFired) {
      navLongFired = false;
      return;
    }
    jumpRelative(direction);
  }

  function showError(): void {
    const message = ui.feedErrors[feed.id];
    if (message) ui.errorModal = { feedId: feed.id, feedName: feed.name, message };
  }

  const categoryIconName = $derived(categoryIcon(feed.category));
  const categoryLabel = $derived.by<string>(() => {
    switch (feed.category) {
      case 'events': return 'Events';
      case 'holidays': return 'Holidays';
      case 'observances': return 'Observances';
      case 'operations': return 'Operations';
      case 'guests': return 'Guests';
      case 'announcements': return 'Announcements';
      case 'travel-international': return 'Travel (International)';
      case 'travel-local': return 'Travel (Local)';
      default: return '';
    }
  });
  const isScratchpad = $derived(feed.source.kind === 'scratchpad');
  // Nav direction hint: when the focused event is at a boundary, the next tap
  // wraps around the list. Signal the cycle with the skip icon pointing toward
  // where it lands — the same fast-forward / rewind glyphs as the jump-to-end
  // flash (prev wraps forward to the last event, next wraps back to the first).
  const navCount = $derived(navEvents.length);
  const focusedHere = $derived(
    focus.feedId === feed.id && focus.eventIndex >= 0 && focus.eventIndex < navCount,
  );
  const nextWraps = $derived(focusedHere && focus.eventIndex >= navCount - 1);
  const prevWraps = $derived(focusedHere && focus.eventIndex <= 0);
  const prevIcon = $derived(
    navFlash === 'prev' ? 'rewind' : prevWraps ? 'fast-forward' : 'chevron-left',
  );
  const nextIcon = $derived(
    navFlash === 'next' ? 'fast-forward' : nextWraps ? 'rewind' : 'chevron-right',
  );
  const prevLabel = 'Previous event (long-press for earliest)';
  const nextLabel = 'Next event (long-press for latest)';
  const errorMessage = $derived(ui.feedErrors[feed.id] ?? null);
  // A fetch in flight only shows while the row has nothing current to show — a
  // first load or a retry after an error — so interval refreshes don't make
  // every title flicker.
  const pending = $derived(
    !!ui.loadingFeeds[feed.id] && (!!errorMessage || events.lastSuccessAt[feed.id] == null),
  );
  const nameTitle = $derived(
    'Tap to expand/collapse · long-press to focus this row · double-tap to edit' +
      (isScratchpad
        ? ''
        : ' · ' +
          formatUpdatedAgo(events.lastSuccessAt[feed.id], clock.now, errorMessage ? 'LAST LOADED' : 'UPDATED').toLowerCase()),
  );
  // Local lanes have no fetched/detected timezone, so fall back to the display
  // timezone — that still gives them the day/night icon and a row clock (the
  // offset label resolves to empty when it matches the display tz).
  const feedTz = $derived(effectiveFeedTz(feed.id) ?? (isScratchpad ? config.timezone : null));
  const tzLabel = $derived(
    feedTz ? formatTzDiff(feedTz, config.timezone, new Date(clock.now), config.dst) : '',
  );
  const feedClockTime = $derived(
    feedTz ? formatTime(new Date(clock.now), config.timeFormat, feedTz as Timezone) : '',
  );
  const morningMin = $derived(dayLimitMinutes(config.morningLimit, 8.5 * 60));
  const eveningMin = $derived(dayLimitMinutes(config.eveningLimit, 20.5 * 60));
  const feedIsDay = $derived(feedTz ? isDaylight(feedTz as Timezone, new Date(clock.now), morningMin, eveningMin) : true);
  // x of the current-time marker as it passes through this row (content
  // coords), so the row clock can hug it: icon left of the line, time right.
  const markerLeft = $derived.by(() => {
    const nowDate = zoom.value === 'month' ? new Date(clock.now) : today.value;
    const base = dateToPx(nowDate, rangeStart, pxPerDay);
    if (!feedTz) return base;
    const mins = tzOffsetMinutesVsDisplay(feedTz, config.timezone, new Date(clock.now), config.dst);
    return base + (mins / 1440) * pxPerDay;
  });
  // Search matches hidden inside a collapsed row, counted on its header.
  const collapsedMatches = $derived(
    feed.collapsed && search.open ? (getMatchCountByFeed().get(feed.id) ?? 0) : 0,
  );
  const debugFlag =
    typeof localStorage !== 'undefined' && localStorage.getItem('calendari.debug') === '1';

  // The icons beside the title move by however far the title's glyphs were
  // rounded off their layout baseline (textBaselineSnap), so they centre on the
  // caps as drawn. Measured again when the name, font size, fonts or rows change.
  let nameTextEl: HTMLElement | undefined = $state();
  let baselineSnapPx = $state(0);
  $effect(() => {
    void feed.name;
    void config.fontSize;
    void textLayout.rev;
    if (!nameTextEl) return;
    watchTextLayout(nameTextEl.closest('.rows'));
    baselineSnapPx = textBaselineSnap(nameTextEl);
  });
</script>

<!-- The keyboard-accessible collapse control is the .name-btn <button> inside
     (its Enter/Space click bubbles to onHeaderClick); the header-level handlers
     just extend the tap target across the whole row header. -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<header
  class="row-header"
  data-collapsed={feed.collapsed ? 'true' : null}
  data-focused={isRowFocused ? 'true' : null}
  data-ink={isInked ? 'true' : null}
  data-kind={feed.kind}
  data-category={feed.category}
  data-feed-id={feed.id}
  onclick={onHeaderClick}
  onpointerdown={onHeaderPointerDown}
  onpointerup={onHeaderPointerEnd}
  onpointercancel={onHeaderPointerEnd}
  onpointerenter={onHeaderPointerEnter}
  onpointerleave={onHeaderPointerLeave}
>
  <!-- Weekend tint + blocking hatch + month rules behind the title, aligned with
       the row bodies (background-attachment:fixed keeps the 45° stripes
       continuous). The title reads over them via its paper stroke. -->
  {#each weekendStrips as w (w.left)}
    <i class="hdr-weekend" data-past={w.past ? 'true' : null} style="left: {w.left}px; width: {w.width}px" aria-hidden="true"></i>
  {/each}
  {#each thickStrips as o (o.left)}
    <i class="hdr-hatch hdr-hatch-thick" style="left: {o.left}px; width: {o.width}px" aria-hidden="true"></i>
  {/each}
  {#each thinStrips as o (o.left)}
    <i class="hdr-hatch hdr-hatch-thin" style="left: {o.left}px; width: {o.width}px" aria-hidden="true"></i>
  {/each}
  {#each dayLines as d (d.px)}
    <i class="hdr-day-line" data-past={d.past ? 'true' : null} style="left: {d.px}px" aria-hidden="true"></i>
  {/each}
  {#each monthLines as m (m.px)}
    <i class="hdr-month-line" data-past={m.past ? 'true' : null} style="left: {m.px}px" aria-hidden="true"></i>
  {/each}
  <div class="lead" style:--baseline-snap="{baselineSnapPx}px">
    {#if isScratchpad}
      <button
        type="button"
        class="scratch-add"
        aria-label="Add event"
        title="Add event"
        onclick={openAddEvent}
      >
        <Icon name="plus" size={14} />
      </button>
    {/if}
    {#if errorMessage}
      <button
        type="button"
        class="warning-btn"
        style="--icon-half: {FEED_ERROR_ICON_PX / 2}px"
        aria-label={'Failed to load ' + feed.name}
        title="Show error"
        onclick={showError}
      >
        <Icon name="help" size={FEED_ERROR_ICON_PX} />
      </button>
    {/if}
    {#if categoryIconName}
      <button
        type="button"
        class="category-mark charm-btn"
        title="{categoryLabel} · long-press to focus this row"
        aria-label="{categoryLabel}: long-press to collapse the other rows"
        onpointerdown={() => charmLongPress.start(focusRowToggle)}
        onpointerup={() => charmLongPress.cancel()}
        onpointercancel={() => charmLongPress.cancel()}
        onpointerleave={() => charmLongPress.cancel()}
      >
        <Icon name={categoryIconName} size={14} />
      </button>
    {/if}
    <button
      type="button"
      class="name-btn"
      ondblclick={openInSettings}
      aria-label="Toggle {feed.name} (double-click to edit)"
      aria-expanded={!feed.collapsed}
      aria-busy={pending}
      title={nameTitle}
    >
      <span class="name-text" bind:this={nameTextEl} data-title-font data-pending={pending ? 'true' : null}><CalendarName name={feed.name} /></span>
      {#if isScratchpad}<LocalBadge size={12} />{/if}
    </button>
    {#if collapsedMatches > 0}
      <span
        class="badge match-badge"
        data-mono
        title="{collapsedMatches} search {collapsedMatches === 1 ? 'match' : 'matches'} in this collapsed calendar"
      >{collapsedMatches}</span>
    {/if}
    {#if debugFlag}
      <span class="badge" data-mono data-debug>{visibleEvents.length}</span>
    {/if}
  </div>
  {#if feedTz}
    <span class="tz-icon" style="left: {markerLeft - 4}px" aria-hidden="true">
      <Icon name={feedIsDay ? 'sun' : 'moon'} size={11} />
    </span>
    <span class="tz-time" data-mono style="left: {markerLeft + 4}px" aria-hidden="true">
      <span>{feedClockTime}</span>
      {#if tzLabel}<span class="tz-offset">({tzLabel})</span>{/if}
    </span>
  {/if}
  <span class="spacer"></span>
  {#if !feed.collapsed}
    <div class="actions">
      <span
        class="nav-wrap"
        role="presentation"
        onpointerdown={() => startNavPress(-1)}
        onpointerup={cancelNavPress}
        onpointercancel={cancelNavPress}
        onpointerleave={cancelNavPress}
      >
        <IconButton
          icon={prevIcon}
          label={prevLabel}
          variant="ghost"
          size={16}
          disabled={navCount <= 1}
          onclick={() => handleNavClick(-1)}
        />
      </span>
      <span
        class="nav-wrap"
        role="presentation"
        onpointerdown={() => startNavPress(1)}
        onpointerup={cancelNavPress}
        onpointercancel={cancelNavPress}
        onpointerleave={cancelNavPress}
      >
        <IconButton
          icon={nextIcon}
          label={nextLabel}
          variant="ghost"
          size={16}
          disabled={navCount <= 1}
          onclick={() => handleNavClick(1)}
        />
      </span>
    </div>
  {/if}
</header>

<style>
  .row-header {
    position: sticky;
    left: 0;
    top: var(--time-header-h);
    display: flex;
    align-items: center;
    padding: 1px 0;
    height: var(--row-header-h);
    background: var(--paper-color);
    border-bottom: var(--border-w) solid var(--weekend-bg);
    z-index: 4;
    width: max-content;
    min-width: 100%;
    box-sizing: border-box;
    /* The whole header is the collapse/expand tap target. */
    cursor: pointer;
    transition: border-color var(--dur-ink) ease;
  }
  .row-header[data-collapsed='true'] {
    border-bottom: var(--border-w) dashed var(--weekend-bg);
  }
  /* A row just focused inks the header's bottom rule (with Row inking its own
     borders) for a moment, then fades back to grey — data-ink is rowInk, lit by
     any row focus (tap, hover, keyboard, Tab) and cleared ROW_INK_FADE_MS on. */
  .row-header[data-ink='true'] {
    border-bottom-color: var(--ink-color);
  }
  /* Weekend tint + blocking hatch behind the header content, mirroring the row
     bodies (Timeline's .weekend-col / .holiday-band and Row's strips). z0 keeps
     them under the sticky .lead / .actions (z1). background-attachment:fixed
     aligns the 45° stripes with the bodies so the pattern reads continuous.
     They reach over the header's own bottom border, as the bodies' tint lies
     over each row's top border: a rule then reads the same inside a weekend as
     outside it (left untinted, the border nearly matched the tinted band and
     vanished there). Not upward — the row's top border is tinted by the body
     band already, and a second layer would darken it. */
  .hdr-weekend,
  .hdr-hatch {
    position: absolute;
    top: 0;
    bottom: calc(-1 * var(--border-w));
    z-index: 0;
    pointer-events: none;
  }
  .hdr-weekend {
    background: color-mix(in srgb, var(--ink-color) 6%, transparent);
  }
  .hdr-weekend[data-past='true'] {
    background: color-mix(in srgb, var(--ink-color) 3%, transparent);
  }
  .hdr-hatch {
    background-attachment: fixed;
    opacity: 0.6;
  }
  .hdr-hatch-thick {
    background-image: repeating-linear-gradient(
      45deg, transparent 0, transparent 4px, var(--holiday-stripe) 4.5px, transparent 5px);
  }
  .hdr-hatch-thin {
    background-image: repeating-linear-gradient(
      45deg, transparent 0, transparent 9px, var(--holiday-stripe) 9.5px, transparent 10px);
  }
  /* Month-separator rule through the header, mirroring the body's .month-line so
     the vertical rule reads continuous down the whole timeline. z0 keeps it
     behind the title (which halos over it via its paper stroke).
     Both header rules reach one border width past the header's top and bottom,
     so they cross the horizontal rules there — the row's top border just above
     and the header's own bottom border — instead of being cut by them: the
     columns read as unbroken lines down the timeline. (Positioned against the
     padding box, so `bottom: -border` lands on the header's own border; the
     header's z4 lifts them over the row's border above.) */
  .hdr-month-line {
    position: absolute;
    top: calc(-1 * var(--border-w));
    bottom: calc(-1 * var(--border-w));
    width: 0;
    border-left: var(--border-w) solid var(--ink-color);
    pointer-events: none;
    z-index: 0;
  }
  .hdr-month-line[data-past='true'] {
    opacity: 0.4;
  }
  /* Day rule through the header, lighter than the month rule, so the 1M day
     columns read continuous down the whole timeline. Fully opaque (no past
     dimming) to match the body's .day-col. */
  .hdr-day-line {
    position: absolute;
    top: calc(-1 * var(--border-w));
    bottom: calc(-1 * var(--border-w));
    width: 0;
    border-left: var(--border-w) solid var(--ink-faint);
    pointer-events: none;
    z-index: 0;
    /* Back over the rules only once their ink has faded (step-end), so the
       faint line never notches a rule still easing back to grey. */
    transition: top var(--dur-ink) step-end, bottom var(--dur-ink) step-end;
  }
  /* A highlighted row inks its rules, which then read darker than a day line:
     the faint day line steps inside the header at once, so it doesn't notch them. */
  .row-header[data-ink='true'] .hdr-day-line {
    top: 0;
    bottom: 0;
    transition: none;
  }
  .lead {
    position: sticky;
    left: 0;
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0 8px;
    /* Transparent so the weekend tint / blocking hatch / month lines run
       continuously through the header; the title stays legible via its paper
       stroke (below), the same halo the event-pill titles use. */
    z-index: 1;
    min-width: 0;
    max-width: calc(100vw - 88px);
  }
  .actions {
    position: sticky;
    right: 0;
    display: flex;
    align-items: center;
    gap: 0.4em;
    padding: 0 var(--row-actions-right, 8px) 0 8px;
    z-index: 1;
    flex-shrink: 0;
    /* No opaque backing: the nav sits over the weekend/hatch/month strips and
       reads via the paper halo below. Hidden until the row is hovered/focused;
       opacity (not display/visibility) keeps the buttons in tab order — so
       tabbing into the row reveals them via :focus-within — and avoids reflow. */
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--dur-ui) ease;
  }
  :global(.row:hover) .actions,
  :global(.row:focus-within) .actions,
  .row-header[data-focused='true'] .actions {
    opacity: 1;
    pointer-events: auto;
  }
  @media (prefers-reduced-motion: reduce) {
    .actions {
      transition: none;
    }
  }
  .nav-wrap {
    display: inline-flex;
  }
  .actions :global(.icon-button) {
    width: var(--row-control-h);
    height: var(--row-control-h);
    /* Paper halo so the glyph stays legible over the strips (the same halo the
       header's clock uses), now that the opaque backing is gone. */
    filter: var(--clock-halo);
  }
  .name-btn {
    flex: 1 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    padding: 2px 8px;
    height: var(--row-control-h);
    font: inherit;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
  }
  .name-btn:focus {
    outline: none;
  }
  .name-text {
    font-size: var(--name-size);
    font-size-adjust: var(--name-x-height);
    font-weight: 400;
    font-family: var(--name-font);
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* The current-day marker's page-colour halo (--clock-halo), so the title
       reads over the weekend/hatch/month-line pattern like the marker's labels. */
    filter: var(--clock-halo);
  }
  .name-text[data-pending='true'] {
    animation: row-pending var(--dur-pulse) ease-in-out infinite;
  }
  @keyframes row-pending {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  /* The row clock hugs the today line but sits under the sticky title (.lead,
     z1): where they meet, the title reads over the clock and the line (the
     timeline's .today-line) over both. Later in the DOM than the strips, so z0
     still paints it above them. */
  .tz-icon {
    position: absolute;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    color: var(--ink-muted);
    transform: translateX(-100%);
    pointer-events: none;
    z-index: 0;
    filter: var(--clock-halo);
    transition: none;
  }
  .tz-time {
    position: absolute;
    top: 0;
    height: 100%;
    display: inline-flex;
    align-items: center;
    gap: 0.3em;
    font-size: var(--fs-11);
    color: var(--ink-muted);
    filter: var(--clock-halo);
    transition: none;
    white-space: nowrap;
    pointer-events: none;
    z-index: 0;
  }
  .tz-offset {
    color: var(--ink-muted);
  }
  .badge {
    font-size: var(--fs-11);
    color: var(--ink-muted);
    background: var(--paper-2);
    padding: 2px 6px;
    border-radius: 999px;
    flex-shrink: 0;
  }
  .match-badge {
    color: var(--paper-color);
    background: var(--accent-color);
  }
  /* The icons left of the title (add, warning, calendar type) take the same
     halo as the title. */
  .lead > button :global(.icon),
  .name-btn :global(.local-badge) {
    filter: var(--clock-halo);
  }
  .category-mark {
    display: inline-flex;
    align-items: center;
    color: var(--ink-color);
    flex-shrink: 0;
  }
  .charm-btn {
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    touch-action: none;
  }
  .charm-btn:focus {
    outline: none;
  }
  .warning-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3em;
    height: var(--row-control-h);
    min-width: var(--row-control-h);
    padding: 0 4px;
    border: none;
    background: transparent;
    color: var(--accent-color);
    cursor: pointer;
    flex-shrink: 0;
  }
  .scratch-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--ink-muted);
    cursor: pointer;
    flex-shrink: 0;
  }
  /* The error, add and category icons centre on the title's cap height, not its
     box: each sits on a text line in the title's face and size, whose baseline
     is aligned with the title's, raised so its middle lands at half the cap
     height (`cap` follows whatever face is drawing: Plex, a fallback, Greek).
     Browsers without the `cap` unit keep `middle`, half the x-height. A line as
     tall as the title's button keeps every box the same height, so nothing
     shifts the row. --icon-half is half the icon's size. */
  .warning-btn,
  .scratch-add,
  .category-mark {
    display: inline-block;
    align-self: baseline;
    height: auto;
    font: italic 400 var(--name-size) / var(--row-control-h) var(--name-font);
    text-align: center;
    white-space: nowrap;
  }
  .warning-btn > :global(.icon),
  .scratch-add > :global(.icon),
  .category-mark > :global(.icon) {
    vertical-align: middle;
    vertical-align: calc(0.5cap - var(--icon-half, 7px));
    /* Follow the title's glyphs, painted on a whole-pixel baseline. */
    translate: 0 var(--baseline-snap, 0px);
  }
  .name-btn {
    align-self: baseline;
  }
  /* Hover/focus text tint comes from the global button rules (accent / --link-color). */
  .spacer {
    flex: 1;
  }
</style>
